import { Box } from '@mui/material'
import MailOutlineIcon from '@mui/icons-material/MailOutline'
import React from 'react'
import Button from '../components/Button'
import ContentWrapper from '../components/ContentWrapper'
import TextField from '../components/TextField'
import Txt from '../components/Txt'
import { renderErrorDialog } from '../components/Dialog'
import requestLoginCode from '../mutations/requestLoginCode'
import codeLogin from '../mutations/codeLogin'
import { handleMutationError } from '../utils/errors'
import { fetchUser, setToken, useCurrentUser } from '../utils/user'
import { saveRefreshCredentials } from '../utils/client'

const RESEND_COOLDOWN_SECONDS = 60
const CODE_LENGTH = 6

const sxCenter = {
	flex: 1,
	width: '100%',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	p: '3rem 1rem',
} as const

const sxCard = {
	width: '100%',
	maxWidth: '24rem',
	p: '2rem',
	bgcolor: 'white',
	borderRadius: '0.75rem',
	boxShadow: 2,
} as const

const sxHint = {
	display: 'flex',
	alignItems: 'flex-start',
	gap: '0.5rem',
	p: '0.75rem',
	borderRadius: '0.5rem',
	bgcolor: 'grey.50',
} as const

// Die Login-Schranke fuer jede eingeloggte Route: nach dem Login wird die Seite gerendert,
// die die Route uebergibt.
const AuthView = ({ children }: { children: JSX.Element }): JSX.Element => {
	const { data: currentUser } = useCurrentUser()

	const [sendingCode, setSendingCode] = React.useState(false)
	const [loggingIn, setLoggingIn] = React.useState(false)
	const [email, setEmail] = React.useState('')
	const [code, setCode] = React.useState('')
	const [codeSent, setCodeSent] = React.useState(false)
	const [cooldownSeconds, setCooldownSeconds] = React.useState(0)

	React.useEffect(() => {
		if (cooldownSeconds === 0) return
		const timeout = setTimeout(() => setCooldownSeconds((seconds) => seconds - 1), 1000)
		return () => clearTimeout(timeout)
	}, [cooldownSeconds])

	const handleChangeEmail = React.useCallback((value: string | number) => {
		setEmail(String(value))
	}, [])

	const handleChangeCode = React.useCallback((value: string | number) => {
		setCode(String(value))
	}, [])

	// Back to step one — der Code wurde an eine andere Adresse geschickt und gilt hier nicht mehr.
	const handleChangeAddress = React.useCallback(() => {
		setCodeSent(false)
		setCode('')
	}, [])

	const handleSendCode = React.useCallback(async () => {
		setSendingCode(true)
		const result = await requestLoginCode({ email })
		setSendingCode(false)
		if (result.errors) {
			handleMutationError('requestLoginCode', result.errors)
		} else {
			setCodeSent(true)
			setCooldownSeconds(RESEND_COOLDOWN_SECONDS)
		}
	}, [email])

	const handleLogin = React.useCallback(async () => {
		setLoggingIn(true)
		const result = await codeLogin({ email, code })
		setLoggingIn(false)
		if (result.errors) {
			handleMutationError('codeLogin', result.errors)
		} else {
			setToken(result.jwt)
			if (result.refreshToken) saveRefreshCredentials(result.refreshToken, result.userId)
			try {
				await fetchUser()
			} catch (e) {
				renderErrorDialog(e.message)
			}
		}
	}, [email, code])

	return currentUser === undefined ? (
		<ContentWrapper />
	) : currentUser !== null ? (
		children
	) : (
		<ContentWrapper center>
			<Box sx={sxCenter}>
				<Box sx={sxCard}>
					{!codeSent ? (
						<>
							<Txt variant="h3">Anmelden</Txt>
							<Txt variant="body2" m="0.5rem 0 1.5rem 0">
								Gib deine E-Mail-Adresse ein — wir schicken dir einen {CODE_LENGTH}-stelligen
								Anmelde-Code.
							</Txt>
							<TextField
								label="E-Mail"
								backgroundColor="white"
								onChange={handleChangeEmail}
								defaultValue={email}
								width="100%"
								autoComplete="username"
							/>
							<Box m="1.5rem 0 0 0">
								<Button
									variant="mainButton"
									onClick={handleSendCode}
									loading={sendingCode}
									disabled={!email}
									fullWidth
									triggerOnEnter
								>
									Anmelde-Code senden
								</Button>
							</Box>
						</>
					) : (
						<>
							<Txt variant="h3">Code eingeben</Txt>
							<Box sx={sxHint} m="1rem 0 1.5rem 0">
								<MailOutlineIcon fontSize="small" />
								<Txt variant="body2">
									{`Falls ein Konto mit ${email} existiert, haben wir einen ${CODE_LENGTH}-stelligen Anmelde-Code dorthin geschickt.`}
								</Txt>
							</Box>
							<TextField
								label="Anmelde-Code"
								backgroundColor="white"
								onChange={handleChangeCode}
								defaultValue={code}
								width="100%"
								// Ein Einmal-Code, kein Passwort — sonst bietet der Passwortmanager an, ihn
								// als Login zu speichern, und fragt danach bei jedem Klick im Dashboard.
								autoComplete="one-time-code"
								inputMode="numeric"
							/>
							<Box m="1.5rem 0 1rem 0">
								<Button
									variant="mainButton"
									onClick={handleLogin}
									loading={loggingIn}
									disabled={code.length < CODE_LENGTH}
									fullWidth
									triggerOnEnter
								>
									Anmelden
								</Button>
							</Box>
							<Box display="flex" flexDirection="row" justifyContent="space-between" gap="1rem">
								<Button
									variant="text"
									onClick={handleSendCode}
									disabled={cooldownSeconds > 0}
									loading={sendingCode}
								>
									{cooldownSeconds > 0 ? `Erneut senden (${cooldownSeconds}s)` : 'Erneut senden'}
								</Button>
								<Button variant="text" onClick={handleChangeAddress}>
									Andere E-Mail-Adresse
								</Button>
							</Box>
						</>
					)}
				</Box>
			</Box>
		</ContentWrapper>
	)
}

export default AuthView
